/// <amd-module name="Build/Run/Components/TaskControllerView" />
import * as React from "react";

import * as Resources from "Build/Run/Resources";

import { YamlSnippetInput, YamlSnippet } from "Pipelines/Common/Library/Contracts/DataProvider";

import { ObservableValue, ObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

import { Icon, IconSize } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { Panel, PanelFooter } from "VSSUI/Panel";
import { ITableColumn, Table, TableCell } from "VSSUI/Table";
import { Observer } from "VSSUI/Observer";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { IVssContributionService } from "VSS/Platform/Contribution";

export interface ITaskControllerViewProps {
    inputs: string;
    onDismiss: () => void;
    pageContext: IVssPageContext;
    taskType: string;
    taskName: string;
    taskVersion: string;
}

const contributionID = "ms.vss-build-web.yaml-assistant-data-provider";

export class TaskControllerView extends React.Component<ITaskControllerViewProps> {
    private _contributionService: IVssContributionService;
    private _hasError: ObservableValue<boolean> = new ObservableValue(false);
    private _inputs: { [key: string]: string } = {};
    private _loading: ObservableValue<boolean> = new ObservableValue(true);
    private _snippetInputs: ObservableArray<YamlSnippetInput> = new ObservableArray();
    private _taskLink: ObservableArray<string> = new ObservableArray();

    constructor(props: ITaskControllerViewProps) {
        super(props);
        this._contributionService = this.props.pageContext.getService<IVssContributionService>("IVssContributionService");
    }

    public componentDidMount() {
        // we just want the major version here
        const version = this.props.taskVersion.split(".")[0];

        this._inputs = JSON.parse(this.props.inputs);

        this._getTaskSnippet(this.props.taskType, version).then(task => {
            if (task) {
                if (task.documentationUrl) {
                    this._taskLink.push(task.documentationUrl);
                }
                this._snippetInputs.value = task.inputs;
                this._hasError.value = false;
            } else {
                this._hasError.value = true;
            }
            this._loading.value = false;
        });
    }

    public render(): JSX.Element {
        const columns: ITableColumn<YamlSnippetInput>[] = [
            {
                id: Resources.Property,
                className: "padding-horizontal-20",
                width: -100,
                name: Resources.Property,
                renderCell: this._renderPropertyCell
            } as ITableColumn<YamlSnippetInput>,
            {
                id: Resources.Value,
                className: "padding-horizontal-20",
                width: -100,
                name: Resources.Value,
                renderCell: this._renderValueCell
            } as ITableColumn<YamlSnippetInput>
        ];

        const helpColumn: ITableColumn<string>[] = [
            {
                id: Resources.HelfpulLinks,
                className: "padding-horizontal-20",
                width: -100,
                name: Resources.HelfpulLinks,
                renderCell: this._renderTaskLink
            } as ITableColumn<string>
        ];

        return (
            <Panel
                backButtonProps={{ onClick: this.props.onDismiss }}
                contentClassName="no-h-padding"
                description={this.props.taskName}
                escDismiss={true}
                onDismiss={this.props.onDismiss}
                titleProps={{
                    text: format(Resources.TaskName, this.props.taskType)
                }}
            >
                <Observer hasError={this._hasError} loading={this._loading}>
                    {(props: { hasError: boolean, loading: boolean }) => {
                        if (!props.hasError && !props.loading) {
                            return (
                                <div className="flex-column">
                                    <Table
                                        className={"task-table"}
                                        columns={columns}
                                        itemProvider={this._snippetInputs}
                                        role="table"
                                        scrollable={true}
                                    />
                                    {this._taskLink.length > 0 && (
                                        <PanelFooter className="no-padding help-col-panel-footer">
                                            <Table
                                                className={"task-table"}
                                                columns={helpColumn}
                                                itemProvider={this._taskLink}
                                                role="table"
                                            />
                                        </PanelFooter>
                                    )}
                                </div>
                            )
                        } else if (!props.loading) {
                            return (
                                <MessageCard severity={MessageCardSeverity.Error} className="flex-grow ">
                                    <span className="body-l">
                                        {Resources.NoTask}
                                    </span>
                                </MessageCard>
                            )
                        } else {
                            return <></>
                        }
                    }}
                </Observer>
            </Panel>
        );
    }

    private async _getTaskSnippet(taskName: string, taskVersion: string): Promise<YamlSnippet | undefined> {
        return this._contributionService.getDataAsync(contributionID, {taskName, taskVersion});
    }

    private _renderPropertyCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<YamlSnippetInput>, tableItem: YamlSnippetInput) => {
        return this._renderCommon(rowIndex, columnIndex, tableColumn, tableItem.label);
    };

    private _renderValueCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<YamlSnippetInput>, tableItem: YamlSnippetInput) => {
        const input = this._inputs[tableItem.name];
        return this._renderCommon(rowIndex, columnIndex, tableColumn, input ?? tableItem.defaultValue);
    };

    private _renderCommon = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<YamlSnippetInput>, textToShow: string) => {
        return (
            <TableCell
                key={"item" + rowIndex + "-" + columnIndex}
                columnIndex={columnIndex}
                className="issue-task-table-padding task-value-cell"
            >
                <div className="flex-grow flex-row">
                    <div className="flex-column flex-grow padding-vertical-8">
                        <span className="primary-text">{textToShow}</span>
                    </div>
                </div>
            </TableCell>
        );
    };

    private _renderTaskLink = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<string>, tableItem: string) => {
        return (
            <TableCell
                key={"task-link" + rowIndex}
                columnIndex={columnIndex}
                className="issue-task-table-padding"
            >
                <div className="flex-grow flex-row">
                    <div className="flex-column flex-grow padding-vertical-8">
                        <Link href={tableItem}>
                            <span className="flex-row">
                                <Icon
                                    className="padding-right-8"
                                    iconName="Unknown"
                                    size={IconSize.medium}
                                />
                                {Resources.AboutTask}
                            </span>
                        </Link>
                    </div>
                </div>
            </TableCell>
        );
    };
}
